import React from 'react';
import Helmet from 'react-helmet';
import { Formik } from 'formik';
import { Elements as StripeElements } from '@stripe/react-stripe-js';
import { Link } from 'react-router-dom';

import RegistrationForm from './components/RegistrationForm';
import RegistrationPhoneVerify from './components/RegistrationPhoneVerify';
import RegistrationPayment from './components/RegistrationPayment';
import SupportText from './components/SupportText';
import useStripePromise from '../../../../hooks/useStripePromise';
import useWeddingsRegistration, {
  WEDDINGS_REGISTRATION_STEP,
} from './useWeddingsRegistration';
import SignUpLayout from '../../../../components/global/SignUpLayout';
import RegistrationEventItemSelection from './components/RegistrationEventItemSelection';
import Button from '../../../../components/global/Button';
import Angle from '../../../../components/global/Angle';
import { isValidEmail } from '../../../../util/email';

const WeddingsRegistration = () => {
  const stripePromise = useStripePromise();
  const {
    step,
    eventItems,
    onPhoneVerificationRequest,
    onPinVerify,
    onPinCodeResend,
    onCheckout,
    loading,
    onEventItemSelected,
    canGoBack,
    onGoBack,
    defaultPromoCode,
    onAccountEmailVerify,
    onAccountPhoneNumberVerify,
    createEventUrl,
    requireHumanInput,
    blockLogin,
  } = useWeddingsRegistration();

  const onSubmit = values => {
    if (step === WEDDINGS_REGISTRATION_STEP.DETAILS) {
      onPhoneVerificationRequest(values);
    }

    if (step === WEDDINGS_REGISTRATION_STEP.PIN_VERIFY) {
      onPinVerify(values);
    }

    if (step === WEDDINGS_REGISTRATION_STEP.CHECKOUT) {
      onCheckout(values);
    }
  };

  const supportText =
    step === WEDDINGS_REGISTRATION_STEP.DETAILS ? <SupportText /> : null;

  const title = (() => {
    if (step === WEDDINGS_REGISTRATION_STEP.SKU_SELECTION) {
      return 'Pick a plan';
    }
    if (step === WEDDINGS_REGISTRATION_STEP.CHECKOUT) {
      return 'Your payment details';
    }
    return 'Create your free WaldoWed gallery';
  })();

  const onValidate = async values => {
    const errors = {};

    switch (step) {
      case WEDDINGS_REGISTRATION_STEP.DETAILS: {
        const { exists: emailExists } = await onAccountEmailVerify(
          values.email_address,
        );
        const { exists: phoneNumberExists } = await onAccountPhoneNumberVerify(
          values.phone_number,
        );

        if (!isValidEmail(values.email_address)) {
          errors.email_address = 'This email address is not valid.';
        }

        if (emailExists) {
          errors.email_address = 'This email address is already taken';
        }

        if (phoneNumberExists) {
          errors.email_address = (
            <span>
              An account with this phone number already exists. Please sign in{' '}
              <Link
                className="weddings-registration-error-link"
                to={`/shutterbug/signin?next=${createEventUrl}`}
              >
                here
              </Link>
              .
            </span>
          );
        }

        break;
      }
      default:
        break;
    }

    return errors;
  };

  return (
    <StripeElements stripe={stripePromise}>
      <Helmet>
        <title>Reserve Waldo for your wedding today!</title>
      </Helmet>

      <SignUpLayout.Wrapper splitPaneClassNames="signup-weddings-promo">
        <SignUpLayout.Content
          size={
            step === WEDDINGS_REGISTRATION_STEP.SKU_SELECTION ? 'xl' : undefined
          }
        >
          <SignUpLayout.Title
            subtitle={
              canGoBack ? (
                <Button
                  onClick={onGoBack}
                  className="btn-body-back d-none d-md-inline-block"
                  btnStyle="link"
                >
                  <Angle direction="left" className="mr-1" /> Back
                </Button>
              ) : undefined
            }
            title={title}
          />
          <Formik
            onSubmit={onSubmit}
            initialValues={{
              first_name: '',
              last_name: '',
              phone_number: '+1',
              promo_code: '',
              default_promo_code: defaultPromoCode ?? '',
              code: '',
              date: null,
              terms_checked: true,
              event_item_id: '',
              is_usa_wedding: true,
              wedding_state_id: undefined,
              email_address: '',
              ignore_checkout: false,

              // Checkout logic
              stripe_token: null,
              is_free_checkout: false,
              // captcha logic
              force_allow_action: false,
            }}
            validate={onValidate}
            validateOnBlur={false}
            validateOnChange={false}
            render={({
              values,
              handleSubmit,
              handleChange,
              handleBlur,
              errors,
              touched,
            }) => {
              const selectedWeddingEventItem = eventItems.find(
                eventItem => eventItem.id === values.event_item_id,
              );

              return (
                <div className="weddings-registration-form-wrapper">
                  {step === WEDDINGS_REGISTRATION_STEP.SKU_SELECTION && (
                    <RegistrationEventItemSelection
                      values={values}
                      handleChange={handleChange}
                      eventItems={eventItems}
                      onEventItemSelected={onEventItemSelected}
                      errors={errors}
                      touched={touched}
                    />
                  )}

                  {step === WEDDINGS_REGISTRATION_STEP.DETAILS && (
                    <RegistrationForm
                      className="mt-4"
                      values={values}
                      handleSubmit={handleSubmit}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      loading={loading}
                      requireHumanInput={requireHumanInput}
                      blockLogin={blockLogin}
                    />
                  )}

                  {step === WEDDINGS_REGISTRATION_STEP.PIN_VERIFY && (
                    <RegistrationPhoneVerify
                      className="mbt-4"
                      loading={loading}
                      handleSubmit={handleSubmit}
                      handleChange={handleChange}
                      onPinCodeResend={onPinCodeResend}
                      values={values}
                      requireHumanInput={requireHumanInput}
                      blockLogin={blockLogin}
                    />
                  )}

                  {step === WEDDINGS_REGISTRATION_STEP.CHECKOUT &&
                    selectedWeddingEventItem && (
                      <RegistrationPayment
                        values={values}
                        submitting={loading}
                        onCheckout={handleSubmit}
                        handleChange={handleChange}
                        eventItem={selectedWeddingEventItem}
                      />
                    )}
                </div>
              );
            }}
          />
          {supportText}

          {step === WEDDINGS_REGISTRATION_STEP.DETAILS && (
            <p className="mb-0 mt-2 text-center">
              Have an account?{' '}
              <Link to="/shutterbug/signin?defaultSignupDisabled=1">
                Sign in &rarr;
              </Link>
            </p>
          )}
        </SignUpLayout.Content>
      </SignUpLayout.Wrapper>
    </StripeElements>
  );
};

export default WeddingsRegistration;
